/**
 * Soft≠Live Page187 Soft≠Live — Response Cases residual liquid
 * (Page39 buses + Page105 Soft≠Live · Page187 Soft≠Live CTAs retained).
 * Mount: /response/cases
 *
 * Buses (isolated — fail closed; no Soft invent cascade):
 *   GET /response/cases/status          always-on posture SoT
 *   GET /incidents                      catalog when list_allowed
 *   GET /incidents/{id}/timeline|sla|proof|review  detail buses
 *   POST …/command/{assign,handoff,override}        admin command bus
 *   PUT  …/review                       PIR write bus
 *   GET /sop/recommendations + feedback when sop_armed
 *
 * Soft residual deleted:
 *  - Soft commander assign toast without POST
 *  - Soft SLA breach invent when clocks absent
 *  - Soft PIR filed invent without PUT
 *  - Soft SOP tip invent when knowledge flag off
 *
 * Rollback: drop residual + /response/cases/status; pins prev v286/168.
 */
import { useMemo, useState, useEffect, useRef } from "react";
import { html, F, M, Ico, KV, Card, Field, Input, Alert, Sec, SOFT } from "./ui.js";
import { path, useApi, useAction, api } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useEventsLive } from "./stream-live.js";
import { softResponseErrorOf } from "./response-live.js";
import { MissionRoomPanel } from "./mission-room-panel.js";
import { CaseFieldMedia } from "./case-field-media.js";
import { LocalEvidenceExport } from "./local-evidence-export.js";
import { softCaseClone, softIncidentId, softQueueClone } from "./response-soft-demo.js";
import { isLocalHost } from "./pathseal.js";
import { boardHeadingOf, caseHeadingOf, looksLikeCode, operatorSay } from "./operator-say.js";

function incidentFromHash() {
  try {
    const h = String((typeof location !== "undefined" && location.hash) || "");
    const q = h.indexOf("?");
    if (q < 0) return "";
    const sp = new URLSearchParams(h.slice(q + 1));
    return String(sp.get("incident") || sp.get("id") || "").trim();
  } catch {
    return "";
  }
}

/** Soft≠Live Page187: missing contract ops must not crash the desk. */
function safeCasesPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_cases_status") return "/response/cases/status";
    if (op === "get__incidents") {
      const lim = (opts && opts.query && opts.query.limit) || 40;
      return `/incidents?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "get__incidents_p_timeline") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/timeline`;
    }
    if (op === "get__incidents_p_sla") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/sla`;
    }
    if (op === "get__incidents_p_proof-of-response") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/proof-of-response`;
    }
    if (op === "get__incidents_p_review") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/review`;
    }
    if (op === "put__incidents_p_review") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/review`;
    }
    if (op === "post__incidents_p_command_assign") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/command/assign`;
    }
    if (op === "post__incidents_p_command_handoff") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/command/handoff`;
    }
    if (op === "post__incidents_p_command_override") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/incidents/${encodeURIComponent(id)}/command/override`;
    }
    if (op === "get__sop_recommendations") {
      const et = (opts && opts.query && opts.query.event_type) || "alarm.trigger";
      return `/sop/recommendations?event_type=${encodeURIComponent(et)}`;
    }
    if (op === "post__sop_p_feedback") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/sop/${encodeURIComponent(id)}/feedback`;
    }
    return "";
  }
}

/** Pure: seat chips from Live Response Cases posture (Page187). */
export function responseCasesSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const lMax = Number(lim.list_max);
  const lDef = Number(lim.list_default);
  const tMax = Number(lim.timeline_paint_max);
  const sMax = Number(lim.sop_paint_max);
  return {
    listAllowed: p.list_allowed === true,
    timelineAllowed: p.timeline_allowed === true,
    slaAllowed: p.sla_allowed === true,
    proofAllowed: p.proof_allowed === true,
    reviewReadAllowed: p.review_read_allowed === true,
    assignAllowed: p.assign_allowed === true,
    handoffAllowed: p.handoff_allowed === true,
    overrideAllowed: p.override_allowed === true,
    pirWriteAllowed: p.pir_write_allowed === true,
    sopReadAllowed: p.sop_read_allowed === true,
    sopFeedbackAllowed: p.sop_feedback_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    writeAllowed: p.write_allowed === true,
    sopArmed: p.sop_armed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    listLimit:
      Number.isFinite(lDef) && lDef > 0
        ? Math.min(lDef, Number.isFinite(lMax) && lMax > 0 ? lMax : 120)
        : 40,
    timelinePaintMax: Number.isFinite(tMax) && tMax > 0 ? Math.floor(tMax) : 40,
    sopPaintMax: Number.isFinite(sMax) && sMax > 0 ? Math.floor(sMax) : 20,
  };
}

/** Pure: timeline rows from envelope. */
export function timelineRowsOf(pack) {
  const root = pack || {};
  const rows = root.items || root.events || (root.timeline && root.timeline.items) || [];
  return Array.isArray(rows) ? rows : [];
}

/** Pure: SLA clocks without inventing breach when fields absent. */
export function slaSummaryOf(pack) {
  const s = pack || {};
  const item = s.item || s.sla || s;
  return {
    state: item.state || item.status || item.sla_state || "—",
    remaining: item.remaining_s != null ? item.remaining_s
      : (item.budget_remaining_s != null ? item.budget_remaining_s : null),
    breached: item.breached === true || item.sla_breached === true,
    target: item.target_s != null ? item.target_s
      : (item.budget_s != null ? item.budget_s : null),
  };
}

/** Pure: clamp catalogue paint — no Soft invent Live rows. */
export function casesRowsClampOf(pack, maxN) {
  const items = (pack && (pack.items || pack.incidents)) || [];
  const list = Array.isArray(items) ? items : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 40;
  return list.slice(0, cap);
}

/** Soft≠Live Page187 — local Mission preview only; never POST as Live. */
const SOFT_CASE_SEED = softCaseClone();

const SOFT_TIMELINE = {
  "soft-inc-p1-gate": [
    { id: "t1", kind: "alarm.trigger", summary: "Perimeter cut · thermal + PIR stack", ts: "21:04:12" },
    { id: "t2", kind: "dispatch.create", summary: "UNIT-7 queued · Soft preview", ts: "21:04:28" },
    { id: "t3", kind: "ai.assess", summary: "Risk 92 · recommend dual response", ts: "21:04:41" },
    { id: "t4", kind: "needs_you", summary: "Seal pedestrian lane · human confirm", ts: "21:05:02" },
  ],
  "soft-inc-p2-plant": [
    { id: "t1", kind: "alarm.trigger", summary: "Heat plume · stack sensor", ts: "20:58:04" },
    { id: "t2", kind: "dispatch.accept", summary: "UNIT-3 accepted · Soft preview", ts: "20:59:11" },
  ],
  "soft-inc-p2-dock": [
    { id: "t1", kind: "alarm.trigger", summary: "Plate mismatch · pad hold", ts: "20:55:40" },
    { id: "t2", kind: "dispatch.en_route", summary: "UNIT-12 en route · Soft preview", ts: "20:56:18" },
  ],
  "soft-inc-p3-lobby": [
    { id: "t1", kind: "panic.latch", summary: "Panic latch · lobby", ts: "20:48:02" },
    { id: "t2", kind: "dispatch.on_scene", summary: "UNIT-1 on scene · Soft preview", ts: "20:51:44" },
    { id: "t3", kind: "proof.wait", summary: "Hold elevators · wait independent proof", ts: "20:52:10" },
  ],
  "soft-inc-p4-yard": [
    { id: "t1", kind: "motion.low", summary: "Low motion · no visual confirm", ts: "20:40:00" },
  ],
  "soft-inc-resolved": [
    { id: "t1", kind: "alarm.trigger", summary: "Stair C trip", ts: "19:10:00" },
    { id: "t2", kind: "closure", summary: "False alarm · contractor badge · Soft only", ts: "19:22:00" },
  ],
};

const SOFT_SLA = {
  "soft-inc-p1-gate": { state: "at_risk", remaining_s: 180, target_s: 600, breached: false },
  "soft-inc-p2-plant": { state: "ok", remaining_s: 420, target_s: 900, breached: false },
  "soft-inc-p2-dock": { state: "ok", remaining_s: 510, target_s: 900, breached: false },
  "soft-inc-p3-lobby": { state: "breached", remaining_s: 0, target_s: 600, breached: true },
  "soft-inc-p4-yard": { state: "watch", remaining_s: 1200, target_s: 1800, breached: false },
  "soft-inc-resolved": { state: "closed", remaining_s: 0, target_s: 600, breached: false },
};

const SOFT_PROOF = {
  "soft-inc-p1-gate": {
    closure: { ok: false, status: "unverified", reason: "Await camera + unit confirm · Soft preview" },
    commander: { username: "chen.wei", assigned_responder: "Chen Wei" },
    dispatches: [{ id: "soft-demo-p1-gate" }],
  },
  "soft-inc-p2-plant": {
    closure: { ok: false, status: "observed", reason: "Hydrant staging Soft preview" },
    commander: { username: "maya.ortiz" },
    dispatches: [{ id: "soft-demo-p2-plant" }],
  },
  "soft-inc-p2-dock": {
    closure: { ok: false, status: "started", reason: "En route Soft preview" },
    commander: { username: "omar.hassan" },
    dispatches: [{ id: "soft-demo-p2-dock" }],
  },
  "soft-inc-p3-lobby": {
    closure: { ok: false, status: "unverified", reason: "Proof blocked until badge reconcile · Soft" },
    commander: { username: "aisha.rahman" },
    dispatches: [{ id: "soft-demo-p3-lobby" }],
  },
  "soft-inc-p4-yard": {
    closure: { ok: false, status: "unverified", reason: "No visual · Soft queue only" },
    commander: {},
    dispatches: [],
  },
  "soft-inc-resolved": {
    closure: { ok: true, status: "verified", reason: "Contractor badge Soft preview — not Live proof" },
    commander: { username: "luis.park" },
    dispatches: [{ id: "soft-demo-resolved" }],
  },
};

const SOFT_REVIEW = {
  "soft-inc-resolved": {
    status: "draft",
    root_cause: "Contractor badge false trip · Soft invent Live PIR forbidden",
    lessons_learned: "Badge window before stair sensors · Soft preview only",
  },
};

const SOFT_SOP = [
  {
    sop_id: "soft-sop-perimeter",
    event_type: "alarm.trigger",
    solution: "Seal pedestrian lane · dual unit · Soft playbook tip (not Live authority).",
  },
  {
    sop_id: "soft-sop-camera",
    event_type: "alarm.trigger",
    solution: "Pull North Gate CAM-04 wall · Soft tip only.",
  },
];

export function CasesDesk({ toast }) {
  const { run, busy, error, envelope } = useAction();

  const posture = useApi(safeCasesPath("get__response_cases_status"));
  const post = posture.data || {};
  const seat = responseCasesSeatOf(posture.loading ? null : post);

  const [forceSoft, setForceSoft] = useState(false);
  const incidents = useApi(
    seat.listAllowed
      ? safeCasesPath("get__incidents", { query: { limit: seat.listLimit } })
      : "",
  );
  const liveRows = useMemo(
    () => (seat.listAllowed ? casesRowsClampOf(incidents.data, seat.listLimit) : []),
    [incidents.data, seat.listAllowed, seat.listLimit],
  );
  const useSoft = forceSoft || softIncidentId(incidentFromHash());
  const rows = useSoft ? SOFT_CASE_SEED : liveRows;

  const [incidentId, setIncidentId] = useState(() => {
    const fromHash = incidentFromHash();
    return fromHash || "";
  });
  const softCase = softIncidentId(incidentId);
  const [caseTab, setCaseTab] = useState("brief");
  const [actionAt, setActionAt] = useState("verify");
  const [commander, setCommander] = useState("");
  const [handoffTo, setHandoffTo] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [lessons, setLessons] = useState("");
  const [actionItems, setActionItems] = useState("");
  const [sopRecs, setSopRecs] = useState([]);
  const [sopBusy, setSopBusy] = useState(false);

  useEffect(() => {
    const fromHash = incidentFromHash();
    if (fromHash && fromHash !== incidentId) {
      setIncidentId(fromHash);
      setCaseTab("brief");
      if (softIncidentId(fromHash)) setForceSoft(true);
    }
    const onHash = () => {
      const id = incidentFromHash();
      if (id) {
        setIncidentId(id);
        setCaseTab("brief");
        if (softIncidentId(id)) setForceSoft(true);
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const timelinePath = incidentId && seat.timelineAllowed && !softCase
    ? safeCasesPath("get__incidents_p_timeline", { pathParams: [incidentId] }) : null;
  const slaPath = incidentId && seat.slaAllowed && !softCase
    ? safeCasesPath("get__incidents_p_sla", { pathParams: [incidentId] }) : null;
  const proofPath = incidentId && seat.proofAllowed && !softCase
    ? safeCasesPath("get__incidents_p_proof-of-response", { pathParams: [incidentId] }) : null;
  const reviewPath = incidentId && seat.reviewReadAllowed && !softCase
    ? safeCasesPath("get__incidents_p_review", { pathParams: [incidentId] }) : null;

  const timeline = useApi(timelinePath);
  const sla = useApi(slaPath);
  const proof = useApi(proofPath);
  const review = useApi(reviewPath);

  /* Case Log: reload timeline when Live events stream advances for this incident. */
  const caseEvents = useEventsLive(15000);
  const caseEventSeen = useRef("");
  useEffect(() => {
    if (softCase || !incidentId) return;
    const events = caseEvents.data && Array.isArray(caseEvents.data.items)
      ? caseEvents.data.items
      : [];
    const hit = events.find((ev) => {
      const iid = String((ev && (ev.incident_id || ev.case_id || ev.event_id)) || "");
      return iid && (iid === incidentId || String(ev.id || "") === incidentId);
    });
    const eid = hit ? String(hit.id || hit.event_id || hit.ts_epoch_ms || "") : "";
    if (!eid || eid === caseEventSeen.current) return;
    caseEventSeen.current = eid;
    timeline.reload && timeline.reload();
    sla.reload && sla.reload();
    proof.reload && proof.reload();
  }, [caseEvents.data, softCase, incidentId, timeline.reload, sla.reload, proof.reload]);

  const softTimeline = softCase ? (SOFT_TIMELINE[incidentId] || []) : null;
  const softSla = softCase ? (SOFT_SLA[incidentId] || { state: "—", remaining_s: null, breached: false }) : null;
  const softProof = softCase ? (SOFT_PROOF[incidentId] || null) : null;
  const softReview = softCase ? (SOFT_REVIEW[incidentId] || null) : null;

  const tRows = useMemo(() => {
    if (softTimeline) return softTimeline.slice(0, seat.timelinePaintMax);
    return timelineRowsOf(timeline.data).slice(0, seat.timelinePaintMax);
  }, [softTimeline, timeline.data, seat.timelinePaintMax]);
  const slaSum = softSla ? {
    state: softSla.state || "—",
    remaining: softSla.remaining_s,
    breached: softSla.breached === true,
    target: softSla.target_s,
  } : slaSummaryOf(sla.data);
  const proofObj = softProof || ((proof.data && (proof.data.item || proof.data)) || null);
  const closure = (proofObj && proofObj.closure) || {};
  const commanderLive = (proofObj && proofObj.commander) || {};
  const sopPaint = Array.isArray(sopRecs) ? sopRecs.slice(0, seat.sopPaintMax) : [];

  const pick = (r) => {
    setIncidentId(String(r.incident_id || r.id || "").trim());
    setCaseTab("brief");
  };

  const reloadCase = () => {
    if (softCase) {
      toast("Soft case · local only · Soft≠Live", "warn");
      return;
    }
    timeline.reload && timeline.reload();
    sla.reload && sla.reload();
    proof.reload && proof.reload();
    review.reload && review.reload();
  };

  const reRead = () => {
    if (useSoft) {
      toast("Practice list never becomes live. Stay on this screen.", "warn");
      return;
    }
    posture.reload && posture.reload();
    incidents.reload && incidents.reload();
    if (incidentId) reloadCase();
    toast("Cases refreshed", "go");
  };

  const savePir = async (status) => {
    if (softCase) {
      toast("Practice write-up never becomes a live close.", "warn");
      return;
    }
    if (!seat.pirWriteAllowed) {
      toast("Write-up is closed for this seat", "warn");
      return;
    }
    if (!incidentId) { toast("Pick a case first", "warn"); return; }
    const items = actionItems.split("\n").map((s) => s.trim()).filter(Boolean);
    const out = await run(safeCasesPath("put__incidents_p_review", { pathParams: [incidentId] }), {
      method: "PUT",
      body: {
        status,
        root_cause: rootCause.trim() || undefined,
        lessons_learned: lessons.trim() || undefined,
        action_items: items.length ? items : undefined,
      },
    });
    if (!out) { toast(error || "Could not save the write-up", "bad"); return; }
    review.reload && review.reload();
    toast(status === "submitted" ? "Write-up submitted" : "Draft saved",
      status === "submitted" ? "go" : "warn");
  };

  const assign = async () => {
    if (softCase) {
      toast("Practice cannot assign a live lead.", "warn");
      return;
    }
    if (!seat.assignAllowed) {
      toast("Assign is closed for this seat", "warn");
      return;
    }
    if (!incidentId || !commander) {
      toast("Pick a case and a person first", "warn");
      return;
    }
    const out = await run(safeCasesPath("post__incidents_p_command_assign", { pathParams: [incidentId] }), {
      body: { commander, responder_username: commander },
    });
    if (!out) { toast(error || "Could not assign the lead", "bad"); return; }
    reloadCase();
    toast("Lead assigned", "go");
  };

  const handoff = async () => {
    if (softCase) {
      toast("Practice cannot hand off a live case.", "warn");
      return;
    }
    if (!seat.handoffAllowed) {
      toast("Handoff is closed for this seat", "warn");
      return;
    }
    if (!incidentId || !handoffTo) {
      toast("Pick a case and who should take it", "warn");
      return;
    }
    const out = await run(safeCasesPath("post__incidents_p_command_handoff", { pathParams: [incidentId] }), {
      body: { to: handoffTo },
    });
    if (!out) { toast(error || "Could not hand off", "bad"); return; }
    reloadCase();
    toast("Handoff → " + handoffTo, "go");
  };

  const override = async () => {
    if (softCase) {
      toast("Practice cannot override a live close.", "warn");
      return;
    }
    if (!seat.overrideAllowed) {
      toast("Override is closed for this seat", "warn");
      return;
    }
    if (!incidentId || String(overrideReason).trim().length < 4) {
      toast("Write why you are overriding (at least a short sentence)", "warn");
      return;
    }
    const out = await run(safeCasesPath("post__incidents_p_command_override", { pathParams: [incidentId] }), {
      body: { reason: overrideReason.trim(), action: "force_close" },
    });
    if (!out) { toast(error || "Could not override", "bad"); return; }
    reloadCase();
    toast("Override recorded", "warn");
  };

  const CASE_TABS = [
    { id: "brief", label: "Command" },
    { id: "evidence", label: "Media" },
    { id: "timeline", label: "Log" },
    { id: "actions", label: "Close" },
  ];
  const CASE_ACTIONS = [
    { id: "verify", label: "Check close", say: "Independent proof before you close this case." },
    { id: "lead", label: "Change lead", say: "Assign, hand off, or force-close." },
    { id: "tips", label: "Tips", say: "Suggested steps only — not orders." },
    { id: "writeup", label: "Write-up", say: "Root cause and lessons after the event." },
    { id: "folio", label: "Export", say: "Designed folio with dispatch, place, AI, and media." },
  ];
  const queueMeta = useMemo(() => {
    if (!useSoft) return new Map();
    const m = new Map();
    softQueueClone().forEach((r) => {
      if (r.incident) m.set(String(r.incident), r);
    });
    return m;
  }, [useSoft]);

  const loadSop = async () => {
    if (softCase) {
      setSopRecs(SOFT_SOP);
      toast("Practice tips never become live knowledge.", "warn");
      return;
    }
    if (!seat.sopReadAllowed) {
      toast(seat.sopArmed
        ? "Tips are closed for this seat"
        : "Suggested steps are not armed on this site", "warn");
      return;
    }
    if (!incidentId) { toast("Pick a case first", "warn"); return; }
    const row = rows.find((r) => String(r.incident_id || r.id || "") === incidentId) || {};
    const eventType = String(row.event_type || row.kind || row.trigger || "alarm.trigger");
    setSopBusy(true);
    try {
      const out = await api(safeCasesPath("get__sop_recommendations", { query: { event_type: eventType } }), {
        method: "GET",
      });
      const list = (out && (out.items || out.recommendations || out.sops
        || (out.recommendation ? [out.recommendation] : null))) || [];
      setSopRecs(Array.isArray(list) ? list : []);
      toast((Array.isArray(list) ? list.length : 0) + " suggested steps", "go");
    } catch (e) {
      setSopRecs([]);
      toast(String((e && e.message) || e || "SOP recommendations unavailable"), "warn");
    } finally {
      setSopBusy(false);
    }
  };

  const sopFeedback = async (id, accepted) => {
    if (softCase) {
      toast("Practice cannot mark a live tip as useful.", "warn");
      return;
    }
    if (!seat.sopFeedbackAllowed) {
      toast("Tip feedback is closed for this seat", "warn");
      return;
    }
    if (!id) return;
    const out = await run(safeCasesPath("post__sop_p_feedback", { pathParams: [id] }), {
      body: { success: !!accepted, accepted: !!accepted },
    });
    if (!out) {
      toast((accepted ? "Accept" : "Reject") + " was refused", "bad");
      return;
    }
    toast(accepted ? "Accept · this tip was useful" : "Reject · this tip was not useful", accepted ? "go" : "warn");
  };

  const alertTone = useSoft
    ? "warn"
    : seat.detail === "cases_command_admin_only" || seat.detail === "sop_knowledge_disabled"
      ? "warn" : "info";
  const alertTitle = !seat.enabled
    ? "Cases closed for this seat"
    : seat.detail === "cases_command_admin_only"
      ? "Lead changes need admin"
      : seat.detail === "sop_knowledge_disabled"
        ? "Tips are off"
        : "Live cases";
  const alertSay = !seat.enabled
    ? "This seat cannot open cases."
    : seat.detail === "cases_command_admin_only"
      ? "You can read the case. Assign / handoff / tips write stay sealed."
      : seat.detail === "sop_knowledge_disabled"
        ? "Case command is Live. Tips stay off until knowledge is armed."
        : "";
  const selectedCase = rows.find((r) =>
    String(r.incident_id || r.id || "").trim() === incidentId,
  ) || null;
  const incidentState = String(
    (selectedCase && (selectedCase.status || selectedCase.state)) || "open",
  );
  const proofState = closure.ok === true
    ? "ready"
    : closure.ok === false
      ? "blocked"
      : String(closure.status || "pending");
  const selectedQueue = incidentId ? (queueMeta.get(incidentId) || null) : null;
  const showLiveSeatAlert = !useSoft && (
    !seat.enabled
    || seat.detail === "cases_command_admin_only"
    || seat.detail === "sop_knowledge_disabled"
  );
  const openCloseTab = (actionId) => {
    setCaseTab("actions");
    setActionAt(actionId || "verify");
  };
  const actionDisabled = (id) => {
    if (softCase) return false;
    if (id === "lead") return !seat.assignAllowed && !seat.handoffAllowed;
    if (id === "tips") return !seat.sopReadAllowed;
    if (id === "writeup") return !seat.reviewReadAllowed;
    if (id === "folio") return false;
    return !seat.proofAllowed;
  };

  return html`<div className="cases-desk cases-desk--liquid cases-desk--v2"
    data-cmc-cases="page39" data-cmc-page="page105-cases" data-cmc-residual="page187-cases"
    data-cmc-cases-ux="rsp-ux-2a" data-soft-demo=${useSoft ? "1" : "0"}>
    <header className="cases-v2__signal">
      <div className="cases-v2__signal-copy">
        <p className="eyebrow">Response</p>
        <h2 className="cases-desk__title">${useSoft ? "Case · practice" : (seat.writeAllowed ? "Case" : "Case · read only")}</h2>
        <p className="note">One case: command, media, log, close.</p>
      </div>
      <div className="cases-v2__modes" role="group" aria-label="Catalogue mode">
        ${isLocalHost() ? html`
          <button type="button" data-on=${useSoft ? "1" : "0"}
            onClick=${() => { setForceSoft(true); setIncidentId(SOFT_CASE_SEED[0].incident_id); setCaseTab("brief"); }}>Soft</button>
          <button type="button" data-on=${!useSoft ? "1" : "0"}
            onClick=${() => { setForceSoft(false); setIncidentId(""); setCaseTab("brief"); }}>Live${liveRows.length ? " · " + liveRows.length : ""}</button>
        ` : null}
        ${useSoft && isLocalHost() ? html`
          <button type="button" className="btn btn--text btn--sm"
            onClick=${() => { setForceSoft(true); setIncidentId(SOFT_CASE_SEED[0].incident_id); setCaseTab("brief"); toast("Soft cases reset", "warn"); }}>Reset</button>
        ` : html`
          <button type="button" className="btn btn--text btn--sm"
            disabled=${busy || incidents.loading || posture.loading || !seat.reReadAllowed}
            onClick=${reRead}><${Ico} d="refresh" w=${13}/> Refresh</button>
          ${envelope ? html`<${KV} k="Last action" v=${operatorSay(envelopeSummary(envelope), "—") || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
        `}
      </div>
    </header>

    ${useSoft
      ? html`<p className="cases-v2__soft-banner">Practice only — Soft writes never become Live.</p>`
      : showLiveSeatAlert && alertSay
        ? html`<${Alert} tone=${alertTone} title=${alertTitle} say=${alertSay}/>`
        : null}
    ${!useSoft && posture.error ? html`<${Alert} tone="warn" title="Could not confirm access" say=${softResponseErrorOf(posture.error)}/>` : null}

    <div className="cases-v2__workspace">
      <aside className="cases-v2__catalog" aria-label="Cases">
        <div className="cases-v2__section-head">
          <div><span>${useSoft ? "Practice" : "Cases"}</span>
            <small>Select one</small></div>
          <b>${rows.length}</b>
        </div>
        ${!useSoft && !seat.listAllowed
          ? html`<p className="note">This seat cannot list cases.</p>`
          : !useSoft && incidents.loading && !incidents.data
            ? html`<p className="note">Loading…</p>`
            : !useSoft && incidents.error
              ? html`<${Alert} tone="warn" title="Could not load cases" say=${softResponseErrorOf(incidents.error)}/>`
              : html`<div className="cases-v2__case-list">
                  ${!rows.length ? html`
                    <div className="cases-v2__empty">
                      <span>No cases</span>
                      <small>${useSoft ? "Reset practice to reload." : "Open cases appear here."}</small>
                    </div>` : null}
                  ${rows.map((r, index) => {
                    const id = String(r.incident_id || r.id || "").trim();
                    const q = queueMeta.get(id);
                    const who = (q && q.who) || "";
                    const reason = (q && q.reason) || "";
                    return html`
                      <${M.button} key=${id} type="button" layout className="cases-v2__case"
                        data-state=${incidentId===id?"on":"off"}
                        data-soft=${r.soft_demo ? "1" : "0"}
                        initial=${{ opacity:0, x:-8 }} animate=${{ opacity:1, x:0 }}
                        transition=${{ ...SOFT, delay:Math.min(index * .035, .18) }}
                        onClick=${()=>pick(r)}>
                        <span className="cases-v2__case-pulse"/>
                        <span className="cases-v2__case-main">
                          <b>${caseHeadingOf(r, q, "This case")}</b>
                          <small>${who && !looksLikeCode(who) ? who + " · " : ""}${boardHeadingOf(r.device_id)}${r.soft_demo ? " · Soft" : ""}</small>
                          ${reason ? html`<em className="cases-v2__case-reason">${operatorSay(reason, "Needs a look")}</em>` : null}
                        </span>
                        <em className="cases-v2__case-state">${operatorSay(r.status || r.state || "open", "Open")}</em>
                      <//>`;
                  })}
                </div>`}
        ${!useSoft ? html`
          <${Field} label="Open a case">
            <${Input} icon="book" value=${incidentId} onChange=${e=>setIncidentId(e.target.value.trim())}/>
          <//>` : null}
      </aside>

      <main className="cases-v2__mission">
        ${!incidentId ? html`
          <div className="cases-v2__mission-empty">
            <p className="eyebrow">Ready</p>
            <h3>Select a case</h3>
            <p>Queue sends people and boards. This file follows <b>one</b> case to close.</p>
          </div>` : html`
          <${F}>
            <section className="cases-v2__incident-head">
              <div className="cases-v2__incident-id">
                <p className="eyebrow">${softCase ? "Practice case" : "Open case"}</p>
                <h3>${caseHeadingOf(selectedCase, selectedQueue, "This case")}</h3>
                <span>${operatorSay(incidentState, "Open")}${selectedCase?.zone || (selectedQueue && selectedQueue.zone) ? " · " + (selectedCase?.zone || selectedQueue.zone) : ""}</span>
                ${selectedQueue && selectedQueue.reason
                  ? html`<p className="cases-v2__incident-reason">${operatorSay(selectedQueue.reason, "Needs a look")}</p>`
                  : null}
              </div>
              <div className="cases-v2__meta" aria-label="Case status">
                <button type="button" className="cases-v2__pill"
                  data-tone=${slaSum.breached ? "bad" : "go"}
                  onClick=${()=>reloadCase()} disabled=${!softCase && !seat.slaAllowed}>
                  <u>Deadline</u>
                  <b>${!softCase && sla.loading ? "…" : slaSum.remaining != null ? String(slaSum.remaining) + "s" : operatorSay(slaSum.state, "—")}</b>
                </button>
                <button type="button" className="cases-v2__pill"
                  data-tone=${proofState === "ready" || proofState === "verified" ? "go" : "warn"}
                  onClick=${()=>openCloseTab("verify")} disabled=${!softCase && !seat.proofAllowed}>
                  <u>Close</u>
                  <b>${operatorSay(proofState, "Pending")}</b>
                </button>
              </div>
            </section>

            <nav className="cases-v2__tabs" role="tablist" aria-label="Case sections">
              ${CASE_TABS.map((t) => html`
                <button key=${t.id} type="button" role="tab"
                  aria-selected=${caseTab === t.id ? "true" : "false"}
                  data-on=${caseTab === t.id ? "1" : "0"}
                  onClick=${() => setCaseTab(t.id)}>${t.label}</button>`)}
            </nav>

            ${caseTab === "brief" ? html`
              <section className="cases-v2__team">
                <${MissionRoomPanel} incidentId=${incidentId} toast=${toast} softDemo=${softCase}/>
              </section>
            ` : null}

            ${caseTab === "evidence" ? html`
              <${CaseFieldMedia} incidentId=${incidentId} softDemo=${!!softCase} toast=${toast}/>
            ` : null}

            ${caseTab === "timeline" ? html`
              <section className="cases-v2__river" aria-label="Case log">
                <div className="cases-v2__section-head">
                  <div><span>Log</span><small>${softCase ? "Practice events" : "What changed"}</small></div>
                  <b>${tRows.length}</b>
                </div>
                ${!softCase && !seat.timelineAllowed
                  ? html`<p className="note">This seat cannot open the log.</p>`
                  : !softCase && timeline.loading && !timeline.data
                    ? html`<p className="note">Loading…</p>`
                    : !softCase && timeline.error
                      ? html`<${Alert} tone="warn" title="Log unavailable" say=${timeline.error}/>`
                      : !tRows.length
                        ? html`<div className="cases-v2__river-empty"><i/>No events yet.</div>`
                        : html`<div className="cases-v2__river-track">
                            ${tRows.map((ev, i) => html`
                              <${M.div} key=${ev.id || ev.ts || i} className="cases-v2__event"
                                initial=${{ opacity:0, y:10 }} animate=${{ opacity:1, y:0 }}
                                transition=${{ ...SOFT, delay:Math.min(i * .04, .2) }}>
                                <i/><span><b>${operatorSay(ev.kind || ev.event_type || ev.action || "event", "Event")}</b>
                                <small>${operatorSay(ev.summary || ev.detail || ev.message, "—")}</small></span>
                                <time>${ev.ts || ev.created_at_ms || ev.at || "—"}</time>
                              <//>`)}
                          </div>`}
              </section>
            ` : null}

            ${caseTab === "actions" ? html`
              <section className="cases-v2__actions">
                <div className="cases-v2__action-nav" role="tablist" aria-label="Close tools">
                  ${CASE_ACTIONS.map((a) => html`
                    <button key=${a.id} type="button" role="tab"
                      data-on=${actionAt === a.id ? "1" : "0"}
                      disabled=${actionDisabled(a.id)}
                      onClick=${() => setActionAt(a.id)}>
                      <b>${a.label}</b>
                      <small>${a.say}</small>
                    </button>`)}
                </div>
                <div className="cases-v2__action-body">
                  ${actionAt === "verify" ? html`
                    <${Card} title="Check close" meta=${softCase ? "Practice" : "Live proof"}>
                      ${!softCase && !seat.proofAllowed ? html`<p className="note">This seat cannot check close.</p>`
                        : !softCase && proof.loading && !proof.data ? html`<p className="note">Loading…</p>`
                        : !softCase && proof.error ? html`<${Alert} tone="warn" title="Unavailable" say=${proof.error}/>`
                        : html`<${F}>
                            <${KV} k="Status" v=${operatorSay(
                              closure.ok === true ? "ready" : closure.ok === false ? "blocked" : (closure.status || closure.reason || "—"),
                              "Pending",
                            )}
                              tone=${closure.ok === true ? "go" : "warn"}/>
                            <${KV} k="Reason" v=${operatorSay(closure.reason, "—")}/>
                            <${KV} k="Lead" v=${String(commanderLive.username || commanderLive.assigned_responder || commanderLive.commander || "—")}/>
                            <${KV} k="Dispatches" v=${Array.isArray(proofObj && proofObj.dispatches)
                              ? String(proofObj.dispatches.length)
                              : proofObj && proofObj.dispatch_count != null ? String(proofObj.dispatch_count) : "—"}/>
                            <p className="note">${softCase
                              ? "Practice proof only — Soft cannot verify Live."
                              : "Sending a person is not enough. Close needs independent proof."}</p>
                          <//>`}
                    <//>`
                  : null}

                  ${actionAt === "lead" ? html`
                    <${Card} title="Change lead" meta=${softCase ? "Practice · writes sealed" : "Live"}>
                      <${KV} k="Current lead" v=${String(commanderLive.username || commanderLive.assigned_responder || commanderLive.commander || "—")}/>
                      <div className="cases-v2__cmd-stack">
                        <${Field} label="New lead"><${Input} icon="users" value=${commander} onChange=${e=>setCommander(e.target.value.trim())}/><//>
                        <button type="button" className="btn btn--go btn--sm" disabled=${busy || (!softCase && !seat.assignAllowed)} onClick=${assign}>Assign lead</button>
                        <${Field} label="Handoff to"><${Input} icon="users" value=${handoffTo} onChange=${e=>setHandoffTo(e.target.value.trim())}/><//>
                        <button type="button" className="btn btn--sm" disabled=${busy || (!softCase && !seat.handoffAllowed)} onClick=${handoff}>Handoff</button>
                      </div>
                      <${Sec} title="Force close" meta="emergency">
                        <${Field} label="Reason (≥4 characters)"><${Input} icon="alert" value=${overrideReason} onChange=${e=>setOverrideReason(e.target.value)}/><//>
                        <button type="button" className="btn btn--danger btn--sm" disabled=${busy || (!softCase && !seat.overrideAllowed)} onClick=${override}>Force close</button>
                      <//>
                      ${error && html`<${Alert} tone="bad" title="Command failed" say=${softResponseErrorOf(error)}/>`}
                    <//>`
                  : null}

                  ${actionAt === "tips" ? html`
                    <${Card} title="Tips" meta="Guidance only">
                      <button className="btn btn--sm btn--ghost" disabled=${sopBusy || (!softCase && !seat.sopReadAllowed)} onClick=${loadSop}>Load tips</button>
                      ${!sopPaint.length ? html`<p className="note">${softCase ? "Load practice tips above." : "No tips loaded."}</p>`
                        : sopPaint.map((it, i) => {
                            const id = String(it.sop_id || it.id || "");
                            return html`<div key=${id || i} className="cases-v2__sop">
                              <${KV} k="Event" v=${String(it.event_type || "—")}/>
                              <p>${it.solution || it.text || it.summary || "—"}</p>
                              <div className="btn-row">
                                <button type="button" className="btn btn--go btn--xs" disabled=${busy || !id || (!softCase && !seat.sopFeedbackAllowed)} onClick=${()=>sopFeedback(id, true)}>Useful</button>
                                <button type="button" className="btn btn--danger btn--xs" disabled=${busy || !id || (!softCase && !seat.sopFeedbackAllowed)} onClick=${()=>sopFeedback(id, false)}>Not useful</button>
                              </div>
                            </div>`;
                          })}
                    <//>`
                  : null}

                  ${actionAt === "folio" ? html`
                    <${Card} title="Case folio" meta=${softCase ? "Practice · local only" : "Designed export"}>
                      <p className="note">${softCase
                        ? "Practice cases still save a local folio. Live IDs stay empty when Soft."
                        : "Includes surveyed address and map plate only when surveyed, dispatch clocks, AI/human, and local stills or recordings."}</p>
                      <${LocalEvidenceExport} incidentId=${incidentId} eventId=${incidentId}/>
                    <//>`
                  : null}

                  ${actionAt === "writeup" ? html`
                    <${Card} title="Write-up" meta="After the event">
                      ${softCase
                        ? html`<${F}><${KV} k="Status" v=${String((softReview && softReview.status) || "—")}/>
                            <${KV} k="Root cause" v=${String((softReview && softReview.root_cause) || "—")}/>
                            <${KV} k="Lessons" v=${String((softReview && softReview.lessons_learned) || "—").slice(0,120)}/><//>`
                        : review.loading && !review.data ? html`<p className="note">Loading…</p>`
                        : review.error ? html`<${Alert} tone="warn" title="Unavailable" say=${review.error}/>`
                        : (() => {
                            const r = (review.data && (review.data.item || review.data.review || review.data)) || {};
                            return html`<${F}><${KV} k="Status" v=${String(r.status || "—")}/>
                              <${KV} k="Root cause" v=${String(r.root_cause || "—")}/>
                              <${KV} k="Lessons" v=${String(r.lessons_learned || "—").slice(0,120)}/><//>`;
                          })()}
                      <${Field} label="Root cause"><${Input} icon="book" value=${rootCause} onChange=${e=>setRootCause(e.target.value)}/><//>
                      <${Field} label="Lessons"><${Input} icon="book" value=${lessons} onChange=${e=>setLessons(e.target.value)}/><//>
                      <${Field} label="Action items" hint="one per line"><textarea className="inp" rows=${4} value=${actionItems} onChange=${e=>setActionItems(e.target.value)}/><//>
                      <div className="btn-row">
                        <button type="button" className="btn btn--sm btn--ghost" disabled=${busy || (!softCase && !seat.pirWriteAllowed)} onClick=${()=>savePir("draft")}>Save draft</button>
                        <button type="button" className="btn btn--go btn--sm" disabled=${busy || (!softCase && !seat.pirWriteAllowed)} onClick=${()=>savePir("submitted")}>Submit</button>
                      </div>
                    <//>`
                  : null}
                </div>
              </section>
            ` : null}
          <//>`}
      </main>
    </div>
  </div>`;
}
